import { useEffect, useMemo, useState } from "react"
import { Loader2, X } from "lucide-react"
import type { IconType } from "react-icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import {
  listPhosphorFillIcons,
  loadPhosphorIcons,
  normalizePhosphorFillName,
  phosphorFillLabel,
} from "../nav/phosphorFillIcons"

const EMPTY_SEARCH_LIMIT = 180
const SEARCH_LIMIT = 240

type PhosphorFillIconPickerProps = {
  value?: string
  onChange: (exportName: string) => void
}

export function PhosphorFillIconPicker({ value, onChange }: PhosphorFillIconPickerProps) {
  const selected = normalizePhosphorFillName(value)
  const [query, setQuery] = useState("")
  const [catalog, setCatalog] = useState<Array<{ name: string; Icon: IconType }>>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    loadPhosphorIcons()
      .then((icons) => {
        if (cancelled) return
        setCatalog(listPhosphorFillIcons(icons))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/\s+/g, "")
    if (!q) return catalog.slice(0, EMPTY_SEARCH_LIMIT)
    return catalog
      .filter((item) => item.name.toLowerCase().includes(q) || phosphorFillLabel(item.name).toLowerCase().replace(/\s+/g, "").includes(q))
      .slice(0, SEARCH_LIMIT)
  }, [catalog, query])

  const selectedMeta = selected ? catalog.find((item) => item.name === selected) : undefined
  const SelectedIcon = selectedMeta?.Icon

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-200">
          {SelectedIcon ? (
            <SelectedIcon className="h-5 w-5" aria-hidden />
          ) : (
            <span className="text-[0.65rem] text-gray-400">None</span>
          )}
        </div>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search filled Phosphor icons…"
          className="h-9"
          aria-label="Search filled Phosphor icons"
        />
        {selected ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => onChange("")}
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </Button>
        ) : null}
      </div>
      {selected ? (
        <p className="text-xs text-gray-500">
          Selected: {phosphorFillLabel(selected)} ({selected})
        </p>
      ) : null}
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-500 py-6 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading filled icons…
        </div>
      ) : (
        <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-900">
          <div className="grid grid-cols-6 gap-1 sm:grid-cols-8">
            {filtered.map(({ name, Icon }) => {
              const active = name === selected
              return (
                <button
                  key={name}
                  type="button"
                  title={phosphorFillLabel(name)}
                  aria-label={phosphorFillLabel(name)}
                  aria-pressed={active}
                  onClick={() => onChange(name)}
                  className={cn(
                    "flex h-9 w-full items-center justify-center rounded-md text-gray-800 dark:text-gray-100 hover:bg-violet-50 dark:hover:bg-violet-950/40",
                    active && "bg-violet-100 ring-2 ring-violet-500 dark:bg-violet-950/70"
                  )}
                >
                  <Icon className="h-5 w-5" />
                </button>
              )
            })}
          </div>
          {!filtered.length ? (
            <p className="py-6 text-center text-xs text-gray-500">No filled icons match that search.</p>
          ) : (
            <p className="pt-2 text-[0.65rem] text-gray-400">
              {query.trim()
                ? `Showing ${filtered.length} filled icon${filtered.length === 1 ? "" : "s"}`
                : `Showing ${filtered.length} of ${catalog.length} filled icons. Search to find more.`}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
